/**
 * localStorage 기반 thread persistence 어댑터.
 *
 * 절대 제약(plan):
 *  - DB / 서버 thread store / `assistant-cloud` 사용 금지
 *  - localStorage only — 키는 호스트가 결정 (예: `pk.workbenchChat.v1`, `pk.playground.v1`)
 *  - 백엔드는 stateless batch fetch 만 — 서버에 thread 가 저장되지 않음
 *
 * 직렬화 형식: `ChatMessage[]` 를 그대로 JSON.stringify.
 *
 * 파싱 실패/quota 초과 등은 silent fallback (load → 빈 배열, save → 무시).
 * 채팅 데이터는 손실되어도 치명적이지 않음 — 호스트가 fetch 로 재구축 가능.
 */
import type { ChatMessage } from "../types.ts"

/** localStorage 가용 여부 — SSR / private mode 대비. */
function isStorageAvailable(): boolean {
	if (typeof window === "undefined") return false
	try {
		const k = "__pk_chat_probe__"
		window.localStorage.setItem(k, "1")
		window.localStorage.removeItem(k)
		return true
	} catch {
		return false
	}
}

/**
 * 저장된 메시지를 읽어온다. 키가 없거나 파싱 실패 시 빈 배열.
 *
 * @param storageKey 저장 키 — 호스트가 결정 (페이지/도메인별 분리)
 * @returns 저장된 ChatMessage 배열 — 없으면 빈 배열
 */
export function loadMessages(storageKey: string): ReadonlyArray<ChatMessage> {
	if (!isStorageAvailable()) return []
	try {
		const raw = window.localStorage.getItem(storageKey)
		if (!raw) return []
		const parsed: unknown = JSON.parse(raw)
		if (!Array.isArray(parsed)) return []
		return parsed as ReadonlyArray<ChatMessage>
	} catch {
		return []
	}
}

/**
 * 메시지 배열을 저장한다. quota 초과 등 실패는 silent.
 *
 * 호출자는 debounce 를 통해 매 토큰 스트림마다 호출하지 않도록 보호한다.
 *
 * @param storageKey 저장 키
 * @param messages 저장할 메시지 배열
 */
export function saveMessages(storageKey: string, messages: ReadonlyArray<ChatMessage>): void {
	if (!isStorageAvailable()) return
	try {
		window.localStorage.setItem(storageKey, JSON.stringify(messages))
	} catch {
		/* quota 초과 / private mode 등 — 채팅은 휘발 OK */
	}
}

/**
 * 저장된 메시지를 모두 지운다. "대화 새로고침" UI 에서 호출.
 *
 * @param storageKey 저장 키
 */
export function clearMessages(storageKey: string): void {
	if (!isStorageAvailable()) return
	try {
		window.localStorage.removeItem(storageKey)
	} catch {
		/* ignore */
	}
}

/**
 * 200ms debounce 헬퍼 — runtime onChange 콜백을 매 토큰마다 호출하지 않도록 보호.
 *
 * @param fn 실행할 함수
 * @param delayMs 지연 시간 (기본 200ms)
 * @returns debounced 함수 + cancel 메서드
 */
export function debounce<TArgs extends unknown[]>(
	fn: (...args: TArgs) => void,
	delayMs = 200
): { (...args: TArgs): void; cancel: () => void } {
	let timer: ReturnType<typeof setTimeout> | undefined
	const debounced = (...args: TArgs) => {
		if (timer) clearTimeout(timer)
		timer = setTimeout(() => {
			timer = undefined
			fn(...args)
		}, delayMs)
	}
	debounced.cancel = () => {
		if (timer) {
			clearTimeout(timer)
			timer = undefined
		}
	}
	return debounced
}
